import { useMutation } from '@graphcommerce/graphql'
import { EnteredOptionInput, ProofInterface } from '@graphcommerce/graphql-mesh'
import { useFormGqlMutationCart, ApolloCartErrorAlert } from '@graphcommerce/magento-cart'
import { CartItemFragment } from '@graphcommerce/magento-cart-items'
import { OrderItemFragment } from '@graphcommerce/magento-customer/components/OrderItem/OrderItem.gql'
import { i18n } from '@lingui/core'
import { Button, Modal, SwipeableDrawer } from '@mui/material'
import { useContext, useEffect, useState } from 'react'
import { AddedToCart as FourOverAddedToCart } from '../../4Over/Minicart/AddedToCart'
import { AttachArtworkDocument } from '../../Artwork/AttachArtwork.gql'
import { HighlightButton } from '../../Button/ButtonStyles'
import { fbqAddToCart } from '../../FacebookPixel/events/fbqAddToCart'
import { gtagAddToCart } from '../../GoogleAnalytics/events/gtagAddToCart'
import { klaviyoAddToCart } from '../../Klaviyo/events/klaviyoAddToCart'
import { Icon } from '../../Layout/Icon'
import { AddedToCart } from '../../Minicart/AddedToCart'
import { globalContext } from '../../NextUi/globalContext'
import { ErrorPrompt } from '../../Prompt/ErrorPrompt'
import { ReviewProofModal } from '../../Proof/ReviewProofModal'
import { OrderItemAdditionalFragment } from '../OrderItem/OrderItemAdditional.gql'
import { ReorderItemDocument } from './ReorderItem.gql'
import { ReorderItemAdjustments } from './ReorderItemAdjustments'

const ReorderButton = (props) =>
  props?.status?.toUpperCase() === 'COMPLETE' ? (
    <HighlightButton variant='contained' className='w-full bg-marigold uppercase' {...props} />
  ) : (
    <Button
      variant='outlined'
      className='w-full rounded-[5px] bg-pure-white uppercase'
      {...props}
    />
  )

export function ReorderItem(props: OrderItemFragment & OrderItemAdditionalFragment) {
  const { id, proofs, product_sku, product_url_key, status } = props
  const [displayProofModal, setDisplayProofModal] = useState(false)
  const [useProof, setUseProof] = useState(false)
  const [attachArtwork] = useMutation(AttachArtworkDocument)
  const { drawer, setDrawer } = useContext(globalContext)
  const [showAddedToCart, setShowAddedToCart] = useState(false)
  const [responseError, setResponseError] = useState<Error | null>(null)
  const [showItemAdjustmentsModal, setShowItemAdjustmentsModal] = useState(false)
  const [failedNeedsAdjustments, setFailedNeedsAdjustments] = useState(false)
  const [adjustedOptions, setAdjustedOptions] = useState<{ uid: string; value: string }[]>([])

  const form = useFormGqlMutationCart(ReorderItemDocument, {
    defaultValues: {
      itemId: id,
    },

    onBeforeSubmit: (values) => {
      const options = [...adjustedOptions]
      setAdjustedOptions([])
      return {
        ...values,
        enteredOptions: options,
      }
    },
    onComplete: ({ data, errors }, vars) => {
      if (!data) {
        return
      }

      if (!data.reorderItem?.addedItemIds?.length) {
        if (
          data.reorderItem?.failedItems
            ?.at(0)
            ?.message.includes('Make sure the options are entered and try again.') &&
          !adjustedOptions.length
        ) {
          setShowItemAdjustmentsModal(true)
          setFailedNeedsAdjustments(true)
        } else if (
          data.reorderItem?.failedItems
            ?.at(0)
            ?.message.includes('Product that you are trying to add is not available.')
        ) {
          setResponseError(new Error('Product no longer exists.'))
        } else {
          setResponseError(new Error('Reorder Failed'))
        }

        return
      }

      setFailedNeedsAdjustments(false)

      const addedItem = data?.reorderItem?.cart.items?.slice(-1)[0]

      if (addedItem) {
        gtagAddToCart(addedItem as unknown as CartItemFragment, 1)
      }

      const category = ''

      fbqAddToCart(addedItem as unknown as CartItemFragment, 1, category || '')
      klaviyoAddToCart(addedItem as unknown as CartItemFragment, 1, category || '')

      const cartItemUid = data?.reorderItem?.cart.items?.slice(-1)[0]?.uid ?? ''

      if (data && proofs && proofs.length > 0) {
        proofs.forEach(async (proof) => {
          if (proof?.id && proof?.file_url && proof?.status === 'Approved') {
            await attachArtwork({
              variables: {
                cartItemUid,
                fileName: proof?.id,
                fileUrl: proof?.file_url,
              },
            })
          }
        })
      }
    },
  })
  const { handleSubmit, register, formState, required, error, setValue, data } = form
  const submitHandler = handleSubmit(() => {})

  useEffect(() => {
    if (useProof && !formState.isSubmitting && !formState.isSubmitted) {
      submitHandler()
        .then(() => {
          setDisplayProofModal(false)
          setUseProof(false)
        })
        .catch(() => {})
    }
  }, [useProof, submitHandler, formState])

  useEffect(() => {
    if (
      !formState.isSubmitting &&
      formState.isSubmitSuccessful &&
      drawer.id === null &&
      !failedNeedsAdjustments
    ) {
      if (!responseError) {
        setShowAddedToCart(true)
      }
      form.reset()
    }
  }, [error, formState, drawer, setDrawer, form, responseError, failedNeedsAdjustments])

  useEffect(() => {
    if (adjustedOptions.length && !formState.isSubmitting) {
      submitHandler()
        .then(() => {})
        .catch(() => {})
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(adjustedOptions), submitHandler, formState])

  const adjustmentsModalSubmitHandler = (newValues: EnteredOptionInput[]) => {
    setAdjustedOptions(newValues)
    setShowItemAdjustmentsModal(false)
  }

  return (
    <div className='mb-1 flex items-center justify-center lg:mb-3'>
      <form onSubmit={submitHandler} noValidate>
        <ReviewProofModal
          isDisplayed={!useProof && displayProofModal}
          setIsDisplayed={setDisplayProofModal}
          proofs={(proofs as ProofInterface[]) ?? []}
          isChatHistoryShown={false}
        >
          <HighlightButton
            type='submit'
            variant='contained'
            size='small'
            // loading={formState.isSubmitting}
            className='add-to-cart w-full bg-marigold uppercase '
            onClick={() => {
              setUseProof(true)
              setDisplayProofModal(false)
            }}
          >
            {i18n._('Approve')}
          </HighlightButton>
        </ReviewProofModal>

        <ApolloCartErrorAlert error={error} />
      </form>
      <ReorderButton
        status={status}
        type='submit'
        size='small'
        onClick={() => {
          if (proofs?.length) {
            setDisplayProofModal(true)
            form.reset()
          } else {
            submitHandler()
              .then(() => {})
              .catch(() => {})
          }
        }}
        loading={formState.isSubmitting}
      >
        {i18n._('Reorder Item')}
      </ReorderButton>

      {responseError && <ErrorPrompt error={responseError} />}

      {!!product_url_key && (
        <Modal
          className='z-10 '
          open={showItemAdjustmentsModal}
          onClose={() => {
            setShowItemAdjustmentsModal(false)
          }}
        >
          <div className='absolute left-[5vw] top-[5vh] w-[90vw] overflow-auto rounded-md border-solid bg-pure-white lg:left-[15vw] lg:w-[70vw]'>
            <div className='sticky top-0 z-20 flex items-center justify-between bg-purple-mist px-[20px] py-[18px] lg:pl-[35px]'>
              <span className='Type-XXXL-Bold flex items-center gap-[10px]' />
              <button
                type='button'
                className='flex cursor-pointer items-center justify-center'
                onClick={() => setShowItemAdjustmentsModal(false)}
              >
                <Icon name='disabled_by_default' className='text-[32px] text-tight-black' />
              </button>
            </div>
            <ReorderItemAdjustments {...props} setAdjustedOptions={adjustmentsModalSubmitHandler} />
          </div>
        </Modal>
      )}

      <SwipeableDrawer
        anchor='right'
        onClose={() => {
          setShowAddedToCart(false)
        }}
        onOpen={() => {}}
        open={showAddedToCart}
        classes={{
          paper: 'rounded-l-[10px] max-w-[520px]',
        }}
      >
        <div>
          {product_sku?.includes('4over') ? (
            <FourOverAddedToCart
              sku={product_sku}
              itemIds={data?.reorderItem?.addedItemIds as string[]}
              onClose={() => {
                setShowAddedToCart(false)
              }}
            />
          ) : (
            <AddedToCart
              sku={product_sku}
              itemIds={data?.reorderItem?.addedItemIds as string[]}
              onClose={() => {
                setShowAddedToCart(false)
              }}
            />
          )}
        </div>
      </SwipeableDrawer>
    </div>
  )
}
